import * as Sentry from "@sentry/browser";
import configureStore from "core-integration/src/configureStore";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";
import App from "./components/App";
import "./i18n";

// In dev mode, check for test URL param to bypass URL filtering
const isTestMode =
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("testError") === "true";

const allowUrls = isTestMode ? undefined : [/nba\.com/, /ismgames\.com/];

if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
  Sentry.init({
    dsn: "https://4d3bce4651ce427e969cdc81592b6c72@o118622.ingest.sentry.io/6726347",
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    allowUrls,
    integrations: [
      new Sentry.BrowserProfilingIntegration(),
      new Sentry.BrowserTracing(),
    ],
    ignoreErrors: [
      /Non-Error promise rejection captured with value:\s*default/i,
      /uc not initted/i,
      /Non-Error promise rejection captured with value: uc not initted/i,
      /Can't find variable: amplitude/i,
      /amplitude is not defined/i,
      /undefined is not an object \(evaluating 'e\.q\.push'\)/,
      /Cannot read properties of undefined \(reading 'DomainData'\)/,
      /Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone/,
      /Non-Error promise rejection captured with value: function undefined/i,
      /Non-Error promise rejection captured with value: Object Not Found Matching Id/i,
    ],

    // beforeSend runs before each event is sent. Return null to drop it.
    beforeSend(event: any) {
      // Drop synthetic non-Error rejection messages (ignoreErrors can miss these).
      const msg = event.exception?.values?.[0]?.value ?? event.message ?? "";
      if (
        /uc not initted/i.test(msg) ||
        /Non-Error promise rejection captured with value:\s*default/i.test(
          msg,
        ) ||
        /Non-Error promise rejection captured with value: function undefined/i.test(
          msg,
        ) ||
        /Non-Error promise rejection captured with value: Object Not Found Matching Id/i.test(
          msg,
        )
      ) {
        return null;
      }

      return event;
    },
    tracesSampleRate: 0.000001,

    tracePropagationTargets: [
      /^https:\/\/nba\.app\/api/,
      /^https:\/\/nba\.staging\.ismgames\.com\/api/,
      /^https:\/\/nbafantasy\.nba\.com\/api/,
    ],

    profilesSampleRate: 0.00001,
  });
}

const container = document.getElementById("root")!;
const root = createRoot(container);

root.render(
  <BrowserRouter>
    <Provider store={configureStore()}>
      <App />
    </Provider>
  </BrowserRouter>,
);
